<script setup lang="ts">
import { Bitcoin, Star, Ticket, Zap } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import RadioButton from 'primevue/radiobutton'
import Tools from '@/utils/tools'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { DynamicModalType } from '@/types'

interface Props {
	ticket: number
	bonus: number
	price: number
	g2a: string
	crypto: boolean
	best: boolean
	limited?: number
	trial: boolean
	usd: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()

const price = computed(() => props.usd ? `$${props.price}` : `${props.price}€`)
const basePrice = computed(() => props.usd ? `$${(0.0223 * props.ticket).toFixed(2)}` : `${(0.0223 * props.ticket).toFixed(2)}€`)
</script>

<template>
	<div class="border border-slate-600 rounded-xl p-6 w-80 bg-gradient-to-b from-slate-900 to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
		<!-- Background accent -->
		<div class="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl" />
		<div class="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl" />

		<!-- Badge for best deal or trial -->
		<div
			v-if="best"
			class="absolute top-3 right-1 bg-gradient-to-r from-pink-500 to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full transform rotate-12 shadow-lg"
		>
			<Star class="w-3 h-3 inline mr-1" />
			BEST DEAL
		</div>

		<div
			v-if="trial"
			class="absolute top-3 right-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full transform rotate-12 shadow-lg"
		>
			<Zap class="w-3 h-3 inline mr-1" />
			TRIAL
		</div>

		<!-- Header with ticket amount -->
		<div class="flex justify-center items-center gap-3 mb-4 relative">
			<div class="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 rounded-xl shadow-md shadow-slate-950">
				<Ticket class="w-6 h-6 !text-white" />
			</div>
			<div class="text-center">
				<div class="text-3xl font-bold text-purple-400">
					{{ Tools.formatNumber(ticket) }}
				</div>
				<div class="text-sm font-medium text-slate-300">
					Tickets
				</div>
			</div>
		</div>

		<!-- Pricing section -->
		<div class="text-center my-6">
			<div
				v-if="bonus"
				class="mb-2 flex-center"
			>
				<span class="font-light line-through text-slate-400 text-lg mr-2">
					{{ basePrice }}
				</span>
				<span class="bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs font-medium">
					{{ Math.round(((0.0223 * ticket - props.price) / (0.0223 * ticket)) * 100) }}% OFF
				</span>
			</div>
			<div class="text-4xl font-bold text-slate-100">
				{{ price }}
			</div>
		</div>

		<!-- Limited quantity warning -->
		<div
			v-if="limited"
			class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 mb-6 text-center"
		>
			<div class="text-yellow-400 font-semibold text-sm">
				⚠️ Limited quantity: {{ limited }} remaining
			</div>
		</div>

		<div class="flex flex-col gap-2 items-center">
			<Button class="button-indigo w-full">
				<Bitcoin class="lucide-button-icon text-black" />
				<span>{{ t('shop.buttons.buy_crypto') }}</span>
			</Button>

			<Button
				class="button-indigo w-full"
				@click="useDynamicModalStore().showModal(DynamicModalType.G2AGuide, {
					props: {
						g2aLink: props.g2a,
					},
				})"
			>
				<img
					src="/icons/g2a.svg"
					class="w-5 h-5"
					alt="G2A"
				>
				<span>{{ t('shop.buttons.buy_g2a') }}</span>
			</Button>

			<span class="text-slate-300 text-xsp text-center">The price on G2A may differ due to additional fees.</span>
			<div class="flex gap-4 text-slate-400 mt-2">
				<i class="pi pi-paypal" />
				<i class="pi pi-bitcoin" />
				<i class="pi pi-credit-card" />
			</div>
		</div>
	</div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>
